import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ScrollText, Zap, Radio, Settings2, User, Play, Pause, ShieldOff, AlertTriangle, Search, RefreshCw, ChevronDown } from "lucide-react";
import { actionLogsAPI, cascadeAPI } from "../api/services";
import Header from "../components/layout/Header";
import { useAuth } from "../context/AuthContext";

const sourceStyles = {
  evenement:      { icon: Radio,     bg: "bg-blue-100",   color: "text-blue-600",   badge: "bg-blue-500" },
  automatisation: { icon: Zap,       bg: "bg-amber-100",  color: "text-amber-600",  badge: "bg-amber-500" },
  fonction:       { icon: Settings2, bg: "bg-teal-100",   color: "text-teal-600",   badge: "bg-teal-500" },
  manuel:         { icon: User,      bg: "bg-slate-100",  color: "text-slate-600",  badge: "bg-slate-500" },
};

const typeStyles = {
  commande: { icon: Play,          color: "text-green-600", label: "Commande" },
  pause:    { icon: Pause,         color: "text-amber-600", label: "Pause" },
  blocage:  { icon: ShieldOff,     color: "text-red-600",   label: "Blocage" },
  erreur:   { icon: AlertTriangle, color: "text-red-600",   label: "Erreur" },
};

function tempsRelatif(dateStr) {
  if (!dateStr) return "";
  const min = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
  if (min < 1) return "À l'instant";
  if (min < 60) return `Il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `Il y a ${h}h`;
  const j = Math.floor(h / 24);
  return `Il y a ${j}j`;
}

export default function Journal() {
  const { t } = useTranslation();
  const { user, adminMode } = useAuth();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterSource, setFilterSource] = useState("Tout");
  const [filterType, setFilterType] = useState("Tout");
  const [filterRev, setFilterRev] = useState("");
  const [filterProj, setFilterProj] = useState("");
  const [revendeurs, setRevendeurs] = useState([]);
  const [projets, setProjets] = useState([]);
  const [page, setPage] = useState(1);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = { page, limit: 50, sort: '-createdAt' };
      if (filterSource !== "Tout") params.source = filterSource;
      if (filterType !== "Tout") params.type = filterType;
      if (filterRev) params.revendeur = filterRev;
      if (filterProj) params.projet = filterProj;
      if (search) params.search = search;
      const res = await actionLogsAPI.getAll(params);
      setLogs(res.data?.data || []);
    } catch (err) {
      console.error("Erreur chargement journal:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await actionLogsAPI.stats();
      setStats(res.data?.data || null);
    } catch (err) {
      console.error("Erreur chargement stats:", err);
    }
  };

  useEffect(() => { fetchLogs(); }, [filterSource, filterType, filterRev, filterProj, search, page]);
  useEffect(() => { fetchStats(); }, []);

  useEffect(() => {
    cascadeAPI.revendeurs().then(res => setRevendeurs(res.data?.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    cascadeAPI.projets(null, filterRev || null).then(res => setProjets(res.data?.data || [])).catch(() => {});
  }, [filterRev]);

  const isAdmin = user?.role === 'admin' && adminMode === 'platform';
  const selectStyle = "px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500";

  return (
    <>
      <Header title={t('journal.title', 'Journal d\'actions')} subtitle={t('journal.subtitle', 'Historique des actions des moteurs de règles')} />

      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
            <p className="text-xs text-slate-400 uppercase tracking-wider">Total</p>
            <p className="text-2xl font-bold text-slate-800 mt-1">{stats.total}</p>
          </div>
          {Object.entries(stats.bySource || {}).map(([source, count]) => {
            const style = sourceStyles[source] || sourceStyles.manuel;
            return (
              <div key={source} className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
                <p className="text-xs text-slate-400 uppercase tracking-wider">{source}</p>
                <p className={`text-2xl font-bold mt-1 ${style.color}`}>{count}</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Filtres */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              placeholder={t('journal.search', 'Rechercher...')}
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <select value={filterSource} onChange={e => { setFilterSource(e.target.value); setPage(1); }} className={selectStyle}>
            <option value="Tout">{t('journal.allSources', 'Toutes les sources')}</option>
            <option value="evenement">{t('journal.event', 'Événement')}</option>
            <option value="automatisation">{t('journal.automation', 'Automatisation')}</option>
            <option value="fonction">{t('journal.function', 'Fonction')}</option>
            <option value="manuel">{t('journal.manual', 'Manuel')}</option>
          </select>

          <select value={filterType} onChange={e => { setFilterType(e.target.value); setPage(1); }} className={selectStyle}>
            <option value="Tout">{t('journal.allTypes', 'Tous les types')}</option>
            <option value="commande">{t('journal.command', 'Commande')}</option>
            <option value="pause">{t('journal.pause', 'Pause')}</option>
            <option value="blocage">{t('journal.block', 'Blocage')}</option>
            <option value="erreur">{t('journal.error', 'Erreur')}</option>
          </select>

          {isAdmin && (
            <select value={filterRev} onChange={e => { setFilterRev(e.target.value); setFilterProj(""); setPage(1); }} className={selectStyle}>
              <option value="">{t('journal.allResellers', 'Tous les revendeurs')}</option>
              {revendeurs.map(r => <option key={r._id} value={r._id}>{r.nom}</option>)}
            </select>
          )}

          <select value={filterProj} onChange={e => { setFilterProj(e.target.value); setPage(1); }} className={selectStyle}>
            <option value="">{t('journal.allProjects', 'Tous les projets')}</option>
            {projets.map(p => <option key={p._id} value={p._id}>{p.nom}</option>)}
          </select>

          <button onClick={() => { fetchLogs(); fetchStats(); }} className="p-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
            <RefreshCw size={16} className="text-slate-500" />
          </button>
        </div>
      </div>

      {/* Liste des logs */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-sm text-slate-400">{t('common.loading', 'Chargement...')}</p>
          </div>
        ) : logs.length === 0 ? (
          <div className="p-12 text-center">
            <ScrollText size={48} className="text-gray-200 mx-auto mb-3" />
            <p className="text-slate-400 font-medium">{t('journal.noLogs', 'Aucune action enregistrée')}</p>
            <p className="text-slate-300 text-sm mt-1">{t('journal.noLogsDesc', 'Les actions des moteurs de règles apparaîtront ici')}</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {logs.map(log => {
              const src = sourceStyles[log.source] || sourceStyles.manuel;
              const tp = typeStyles[log.type] || typeStyles.commande;
              const SrcIcon = src.icon;
              const TpIcon = tp.icon;

              return (
                <div key={log._id} className="px-5 py-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start gap-4">
                    {/* Source icon */}
                    <div className={`w-10 h-10 ${src.bg} rounded-xl flex items-center justify-center shrink-0`}>
                      <SrcIcon size={18} className={src.color} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm text-slate-800">{log.sourceNom || log.source}</span>
                        <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full font-medium text-white ${src.badge}`}>
                          {log.source}
                        </span>
                        <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${tp.color}`}>
                          <TpIcon size={12} />
                          {tp.label}
                        </span>
                      </div>

                      {/* Action + Actionneurs */}
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        {log.action && (
                          <span className="text-[11px] bg-gray-100 text-slate-700 px-2 py-0.5 rounded-md font-medium">{log.action}</span>
                        )}
                        {log.actionneurs && (
                          <span className="text-[11px] bg-orange-100 text-orange-700 px-2 py-0.5 rounded-md">{log.actionneurs}</span>
                        )}
                        {log.bloquePar && (
                          <span className="text-[11px] bg-red-100 text-red-700 px-2 py-0.5 rounded-md">Bloqué par: {log.bloquePar}</span>
                        )}
                      </div>

                      {/* Details */}
                      {log.details && (
                        <p className="text-xs text-slate-400 mt-1 truncate">{log.details}</p>
                      )}
                    </div>

                    {/* Timestamp + Projet */}
                    <div className="text-right shrink-0">
                      <p className="text-xs text-slate-400">{tempsRelatif(log.createdAt)}</p>
                      {log.projet?.nom && (
                        <p className="text-[11px] text-slate-300 mt-0.5">{log.projet.nom}</p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {logs.length > 0 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
            <button
              disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium ${page <= 1 ? 'text-gray-300 cursor-not-allowed' : 'text-blue-600 hover:bg-blue-50'}`}
            >
              {t('journal.prev', 'Précédent')}
            </button>
            <span className="text-sm text-slate-400">{t('journal.page', 'Page')} {page}</span>
            <button
              disabled={logs.length < 50}
              onClick={() => setPage(p => p + 1)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium ${logs.length < 50 ? 'text-gray-300 cursor-not-allowed' : 'text-blue-600 hover:bg-blue-50'}`}
            >
              {t('journal.next', 'Suivant')}
            </button>
          </div>
        )}
      </div>
    </>
  );
}
